import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getSavedSuppliers, removeSavedSupplier, getFavoriteProducts, removeFavoriteProduct } from '@/lib/api';
import { getFirstImage } from '@/utils/imageUtils';
import { 
  Heart, 
  Building2, 
  MapPin, 
  Trash2,
  CheckCircle2,
  Package,
  Store,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const FavoritesPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('suppliers');
  const { isUSA, formatPrice } = useLocale();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [suppliersData, productsData] = await Promise.all([
        getSavedSuppliers(),
        getFavoriteProducts()
      ]);
      setSuppliers(suppliersData || []);
      setProducts(productsData || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSupplier = async (companyId) => {
    try {
      await removeSavedSupplier(companyId);
      setSuppliers(suppliers.filter(s => s.company_id !== companyId));
      toast.success(isUSA ? 'Supplier removed from favorites' : 'Proveedor eliminado de favoritos');
    } catch (error) {
      toast.error(isUSA ? 'Error removing supplier' : 'Error al eliminar proveedor');
    }
  };

  const handleRemoveProduct = async (productId) => {
    try {
      await removeFavoriteProduct(productId);
      setProducts(products.filter(p => p.product_id !== productId));
      toast.success(isUSA ? 'Product removed from favorites' : 'Producto eliminado de favoritos');
    } catch (error) {
      toast.error(isUSA ? 'Error removing product' : 'Error al eliminar producto');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  const totalFavorites = suppliers.length + products.length;

  return (
    <div data-testid="favorites-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">
          {isUSA ? 'Favorites' : 'Favoritos'}
        </h1>
        <p className="text-slate-500 mt-1">
          {totalFavorites} {isUSA ? 'saved items' : 'elementos guardados'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="suppliers" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {isUSA ? 'Suppliers' : 'Proveedores'} ({suppliers.length})
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            {isUSA ? 'Products' : 'Productos'} ({products.length})
          </TabsTrigger>
        </TabsList>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers">
          {suppliers.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">
                {isUSA ? "No saved suppliers" : 'Sin proveedores guardados'}
              </h3>
              <p className="text-slate-500 mt-1 mb-4">
                {isUSA ? 'Save suppliers to access them easily' : 'Guarda proveedores para acceder a ellos fácilmente'}
              </p>
              <Link to="/search">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  {isUSA ? 'Search suppliers' : 'Buscar proveedores'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppliers.map((company) => (
                <Card key={company.company_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {company.logo_url ? (
                        <img 
                          src={company.logo_url} 
                          alt={company.company_name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 truncate text-sm">
                            {company.company_name}
                          </h3>
                          {company.verified && (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {isUSA 
                            ? (company.company_type === 'proveedor' ? 'Supplier' : 
                               company.company_type === 'fabricante' ? 'Manufacturer' : 'Supplier & Manufacturer')
                            : (company.company_type === 'proveedor' ? 'Proveedor' : 
                               company.company_type === 'fabricante' ? 'Fabricante' : 'Proveedor y Fabricante')
                          }
                        </p>
                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                          <MapPin className="w-3 h-3" />
                          {company.location_city}, {company.location_state}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex gap-2">
                      <Link to={`/company/${company.company_id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full text-xs">
                          {isUSA ? 'View' : 'Ver'}
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRemoveSupplier(company.company_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products">
          {products.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">
                {isUSA ? "No saved products" : 'Sin productos guardados'}
              </h3>
              <p className="text-slate-500 mt-1 mb-4">
                {isUSA ? 'Save products to compare or purchase later' : 'Guarda productos para compararlos o comprar después'}
              </p>
              <Link to="/search">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  {isUSA ? 'Search products' : 'Buscar productos'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map((product) => (
                <Card key={product.product_id} className="hover:shadow-md transition-shadow overflow-hidden">
                  <div className="aspect-square relative bg-slate-100">
                    {product.images && product.images[0] ? (
                      <img 
                        src={getFirstImage(product.images)} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    {product.source === 'store' && (
                      <div className="absolute top-2 left-2 bg-emerald-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Store className="w-3 h-3" />
                        {isUSA ? 'Store' : 'Tienda'}
                      </div>
                    )}
                    <button 
                      onClick={() => handleRemoveProduct(product.product_id)}
                      className="absolute top-2 right-2 bg-white/90 p-1.5 rounded-full text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Heart className="w-4 h-4 fill-current" />
                    </button>
                  </div>
                  <CardContent className="p-3">
                    <h3 className="font-medium text-slate-900 text-sm truncate">
                      {product.name}
                    </h3>
                    {product.store_name && (
                      <p className="text-xs text-slate-500 truncate">
                        {product.store_name}
                      </p>
                    )}
                    {product.company_name && (
                      <p className="text-xs text-slate-500 truncate">
                        {product.company_name}
                      </p>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      {product.price && !product.hide_price ? (
                        <span className="font-semibold text-emerald-600">
                          {formatPrice(product.price)}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">
                          {isUSA ? 'Price on request' : 'Precio bajo cotización'}
                        </span>
                      )}
                    </div>
                    <Link 
                      to={product.source === 'store' && product.store_slug 
                        ? `/tienda/${product.store_slug}` 
                        : `/product/${product.product_id}`
                      }
                      className="mt-2 block"
                    >
                      <Button variant="outline" size="sm" className="w-full text-xs">
                        {isUSA ? 'View product' : 'Ver producto'}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FavoritesPage;
